import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useBusiness } from './BusinessContext';
import { useAuth } from './AuthContext';
import { DEFAULT_UI_FEATURE_POLICY } from '../config/uiFeaturePolicy.defaults';
import { canAccessFeature } from '../utils/featureAccessResolver';
import { loadFeaturePolicy, saveFeaturePolicy } from '../services/uiFeaturePolicyApi';
import type { UiFeaturePolicy } from '../types/uiAccessPolicy';

type FeatureAccessContextValue = {
  policy: UiFeaturePolicy;
  loading: boolean;
  error: string | null;
  reloadPolicy: () => Promise<void>;
  updatePolicy: (next: UiFeaturePolicy) => Promise<void>;
  canAccess: (featureKey: string) => boolean;
};

const FeatureAccessContext = createContext<FeatureAccessContextValue | null>(null);

export function FeatureAccessProvider({ children }: { children: ReactNode }) {
  const { activeConfigId } = useBusiness();
  const { user } = useAuth();
  const [policy, setPolicy] = useState<UiFeaturePolicy>(DEFAULT_UI_FEATURE_POLICY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reloadPolicy = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const loaded = await loadFeaturePolicy();
      setPolicy(loaded);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load UI feature policy');
      setPolicy(DEFAULT_UI_FEATURE_POLICY);
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePolicy = useCallback(async (next: UiFeaturePolicy) => {
    setError(null);
    const saved = await saveFeaturePolicy(next);
    setPolicy(saved);
  }, []);

  useEffect(() => {
    void reloadPolicy();
  }, [reloadPolicy]);

  const canAccess = useCallback(
    (featureKey: string) =>
      canAccessFeature(policy, featureKey, {
        configId: activeConfigId,
        userId: user?.id ?? null,
        userRole: user?.role ?? null,
      }),
    [policy, activeConfigId, user?.id, user?.role],
  );

  const value = useMemo<FeatureAccessContextValue>(
    () => ({ policy, loading, error, reloadPolicy, updatePolicy, canAccess }),
    [policy, loading, error, reloadPolicy, updatePolicy, canAccess],
  );

  return <FeatureAccessContext.Provider value={value}>{children}</FeatureAccessContext.Provider>;
}

export function useFeatureAccess(): FeatureAccessContextValue {
  const ctx = useContext(FeatureAccessContext);
  if (!ctx) throw new Error('useFeatureAccess must be used within FeatureAccessProvider');
  return ctx;
}
